import { useCallback, useEffect, useState } from "react";
import { fetchDay } from "@/lib/api";
import { dueEntries } from "./useSrs";
import type { SrsRecord } from "@/lib/srs";
import type { Word } from "@/types/lex";

export interface ReviewItem extends Word {
  day: number;
  theme_en: string;
  theme_vi: string;
  record: SrsRecord;
}

type Status = "loading" | "ready" | "error";

interface ReviewQueueState {
  status: Status;
  items: readonly ReviewItem[];
  error: Error | null;
  /** Rebuild from the current schedule — for "Review again" after a session. */
  reload: () => void;
}

/**
 * Snapshot of everything due right now, most overdue first.
 *
 * Deliberately *not* subscribed to the schedule store: a session must keep the
 * queue it started with. Grading a card writes to the store, and if this hook
 * rebuilt on every write the Review screen would unmount mid-session and start
 * over from the first card. Call `reload()` to pick up a new queue on purpose.
 */
export function useReviewQueue(): ReviewQueueState {
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<{
    status: Status;
    items: readonly ReviewItem[];
    error: Error | null;
  }>({ status: "loading", items: [], error: null });

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const due = dueEntries(Date.now());

    if (due.length === 0) {
      setState({ status: "ready", items: [], error: null });
      return;
    }

    setState({ status: "loading", items: [], error: null });
    const days = Array.from(new Set(due.map((d) => d.day)));

    Promise.all(days.map((d) => fetchDay(d)))
      .then((results) => {
        if (cancelled) return;
        const byDay = new Map(results.map((r) => [r.day, r]));
        const items: ReviewItem[] = [];
        for (const entry of due) {
          const dayData = byDay.get(entry.day);
          const word = dayData?.words.find((w) => w.index === entry.index);
          if (!dayData || !word) continue;
          items.push({
            ...word,
            day: entry.day,
            theme_en: dayData.theme_en,
            theme_vi: dayData.theme_vi,
            record: entry.record,
          });
        }
        setState({ status: "ready", items, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          items: [],
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return { ...state, reload };
}
